import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { notification, Row, Col, Card, Descriptions, Typography, Table, Skeleton, Button } from "antd";
import { AppstoreFilled } from "@ant-design/icons";

import { getGapsByCode } from "../../API/gaps";
import { getIssueByCode, updateIssueByCode, getIssueStatus } from "../../API/gaps";
import { getControlsArea } from "../../API/controls";

import WithPermission from "../Common/WithPermission/WithPermission";
import BackButton from "../Common/BackButton/BackButton";
import BreadCrumb from "../Common/BreadCrumb/BreadCrumb";
import ChangeLog from "../Common/ChangeLog/ChangeLog";
import PageTitle from '../Common/PageTitle/PageTitle';
import Assetia from '../Common/Assetia/Assetia';

import GapsDetailModal from "./components/GAPsDetailModal/GapsDetailModal";
import AssignTasks from "./components/GAPsDetailTasks/components/AssignTasks/AssignTasks";
import ViewTasks from "./components/GAPsDetailTasks/components/ViewTasks/ViewTasks";

import Columns from  './components/GAPsDetailTable/columns'; 

import { useAuth } from "../Context/AuthContext";

import './Gaps.css';

const { Title, Text } = Typography;

const GapsDetail = () => {
  // Permisos
  const { hasPermission, userData, logout } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  // Consulta permisos: Tiene rol asignado  
  const hasAnyRole = userData?.roles?.length > 0;
  
  const { code } = useParams();
  const navigate = useNavigate();

  const [api, contextHolder] = notification.useNotification();
  const [loading, setLoading] = useState(true);
  const [gapDetails, setGapDetails] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueDetails, setIssueDetails] = useState(null);
  const [issueStatus, setIssueStatus] = useState(null);
  const [issueAreas, setIssueAreas] = useState(null)
  const [tasks, setTasks] = useState([]);

  // Consulta especifica de permisos
  const canGapsRead = userData ? hasPermission("gaps", "read", gapDetails?.area?.id) : false;
  const canGapsUpdate = userData ? hasPermission("gaps", "update", gapDetails?.area?.id) : false;

  // Cargar detalles del GAP
  const fetchGapDetails = async () => {
    try {
      setLoading(true);
      const response = await getGapsByCode(code); 

      const issuesWithFlag = response.issues.map(issue => ({
        ...issue,
        isFinished: issue.status?.name === "Finalizado" || !!issue.date_finished
      }));

      setGapDetails({
        ...response,
        issues: issuesWithFlag
      });;
    } catch {
      api.error({ message: "Error al cargar los detalles del GAP" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGapDetails();
  }, [code]); 

  const breadCrumbUrl = [
    { label: 'GAPs', url: '/gaps' },
    { label: gapDetails?.status || '' },
  ];

  const goToBioFunctions = () => {
    navigate(-1);
  };

  // Función unificada para mostrar modales
  const showModal = async (record, modalType) => {
    try {
      setSelectedIssue(record);
      setActiveModal(modalType);
      setIsModalVisible(true);
      setModalLoading(true);

      // Cargar datos comunes para todos los modales
      const areasPromise = getControlsArea("tasks");
      
      // Cargar datos específicos según el tipo de modal
      if (modalType === 'detail') {
        const [details, status, areas] = await Promise.all([
          getIssueByCode(record.code),
          getIssueStatus(record.code),
          areasPromise
        ]);
        setIssueDetails(details);
        setIssueStatus([...status].sort((a, b) => a.index - b.index));
        setIssueAreas(areas);
      } else if (modalType === 'view') {
        const [details, status, areas] = await Promise.all([
          getIssueByCode(record.code),
          getIssueStatus(record.code), 
          areasPromise
        ]);
        setTasks((details.tasks || []).map(task => ({
          id: task.id,
          code: task.code,
          name: task.name,
          description: task.description,
          assigned_to: task.username || task.user?.username, 
          status: task.status,
          date_committed: task.date_committed,
          date_finished: task.date_finished,
          user: task.user,
          responsible_area: task.responsible_area,
          comments: task.comments || []
        })));
        setIssueStatus([...status].sort((a, b) => a.index - b.index));
        setIssueAreas(areas);
      }
      else { // modalType === 'assign'
        const areas = await areasPromise;
        setIssueAreas(areas);
      }
    } catch (error) {
      api.error({ 
        message: `Error al cargar los datos para ${modalType}`,
        description: error.message
      });
    } finally {
      setModalLoading(false);
    }
  };

  // Función unificada para cerrar modales
  const handleModalClose = () => {
    setIsModalVisible(false);
    setActiveModal(null);
    if (activeModal === 'detail') {
      setIssueDetails(null);
      setIssueStatus(null);
      setIssueAreas(null);
    }
  };

  // Función para manejar cuando se agrega una nueva tarea
  const handleTaskAdded = (newTask) => {
    setTasks(prev => [...prev, {
      code: newTask.code,
      description: newTask.description,
      assigned_to: newTask.username,
      status: newTask.status
    }]);
  };

  const handleSave = async (formData) => {
    try {
      setModalLoading(true);
      
      // Llamada a la API para guardar los cambios
      const updatedIssue = await updateIssueByCode(selectedIssue.code, formData);
      
      // Actualizar el estado local
      setGapDetails(prev => {
        const updatedIssues = prev.issues.map(issue => 
          issue.code === selectedIssue.code ? 
          { ...issue, ...updatedIssue } : 
          issue
        );
        
        return { ...prev, issues: updatedIssues };
      });
      setSelectedIssue(prev => ({ ...prev, ...updatedIssue }));
      
      // Cerrar el modal y limpiar estados
      setIsModalVisible(false);
      setIssueDetails(null);
      setIssueStatus(null);
      setIssueAreas(null);
      
      return true;
    } catch (error) {
      console.error('Error al guardar:', error);
      api.error({ message: "Error al guardar los cambios", key: 'save-gap-error' });
      throw error;
    } finally {
      setModalLoading(false);
    }
  };

  const [changeLogVisible, setChangeLogVisible] = useState(false);
  const [changeLogData, setChangeLogData] = useState(null);
  const [changeLogLogs, setChangeLogLogs] = useState([]);

  const handleOpenChangeLog = async (record) => {
    try {
      const evaluation = await getIssueByCode(record.code);
      setChangeLogData(record);
      setChangeLogLogs(evaluation.logs || []);
      setChangeLogVisible(true); 
    } catch (error) {
      api.error({ message: "Error al obtener logs", description: error.message });
    }
  };

  const handleCloseChangeLog = () => {
    setChangeLogVisible(false);
    setTimeout(() => {
      setChangeLogData(null);
      setChangeLogLogs([]);
    }, 300);
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      hasAnyRole={hasAnyRole}
      requiredPermission="read"
      currentPermission={canGapsRead}
      redirectTo="/home"
    >
      <>
        {contextHolder}
          <Row justify="space-between">
            <Col>
              <PageTitle title="Detalle de GAP" icon="gaps" />
            </Col>
            <Col>
              <ChangeLog data={gapDetails} logs={gapDetails?.logs || []} />
            </Col>
          </Row>
          
          <Row justify="start" align="middle" gutter={16}>
            <Col style={{ display: 'flex', alignItems: 'center' }}>
              <BackButton onConfirm={goToBioFunctions} />
              <BreadCrumb location={breadCrumbUrl} activeKey={code} />
            </Col>
          </Row>

          {!gapDetails ? (
            <>
              <Skeleton active paragraph={{ rows: 6 }} />
                <div style={{ height: 32 }} />
              <Skeleton active paragraph={{ rows: 6 }} />
            </>
          ) : (
            <>
              <Row gutter={[16, 16]} justify="center">
                {/* Card de Información General */}
                <Col xs={24} md={24}>
                  <Card hoverable className="detail-card">
                    <div /* className="card-content" */>
                      {/* <InfoCircleFilled className="icon-large icon-custom" /> */}
                      <div className="text-content">
                        <Title className="title-custom" level={4}>GAP</Title>
                        <Descriptions bordered column={1} size="middle">
                          <Descriptions.Item label="Nombre del control">{gapDetails.dependency_control.control.title}</Descriptions.Item>
                          <Descriptions.Item label="Descripción del control">{gapDetails.dependency_control.control.description}</Descriptions.Item>
                          <Descriptions.Item label="Dependencia del control">
                            {gapDetails.dependency_control ? gapDetails.dependency_control.dependency.name : 'N/A'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Tipo">{gapDetails.type}</Descriptions.Item>
                          <Descriptions.Item label="Estado">{gapDetails.status}</Descriptions.Item>
                          <Descriptions.Item label="Responsable">{gapDetails.area.name}</Descriptions.Item>
                        </Descriptions>
                      </div>
                    </div>
                  </Card>
                </Col>

                {/* Card de Issues Asociados */}
               <Col xs={24} md={24}>
                <Card hoverable className="detail-card">
                  <div className="text-content">
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Title className="title-custom" level={4}>
                          Issues Asociados
                        </Title>
                      </Col>
                      {/* {isSuperuser && (
                        <Col>
                          <Button
                            type="primary"
                            className="view-deleted-button"
                            style={{ marginBottom: 10 }}
                            onClick={() => navigate("/issues-deleted")}
                          >
                            <AppstoreFilled />
                            Ver Borrados
                          </Button>
                        </Col>
                      )} */}
                    </Row>

                    {gapDetails.issues.length > 0 ? (
                      <Table
                        dataSource={gapDetails.issues}
                        columns={Columns({ showModal, isSuperuser, hasAnyRole, canGapsRead, canGapsUpdate, handleOpenChangeLog })}
                        rowKey={(record) => record.code || record.id || Math.random()}
                        pagination={false}
                        bordered
                        size="middle"
                      />
                    ) : (
                      <Text type="secondary">No hay issues asociados.</Text>
                    )}
                  </div>
                </Card>
              </Col>

              </Row>
            </>)
          }

          <ChangeLog
            open={changeLogVisible}
            onClose={handleCloseChangeLog}
            data={changeLogData}
            logs={changeLogLogs}
          />

          {/* Modales */}
          <GapsDetailModal
            visible={isModalVisible && activeModal === 'detail'}
            //visible={isModalVisible && activeModal === 'detail' && issueDetails}
            onCancel={handleModalClose}
            onSave={handleSave}
            selectedIssue={selectedIssue}
            issueDetails={issueDetails}
            issueStatus={issueStatus}
            issueAreas={issueAreas}
            loading={modalLoading}
            api={api}
            gapDetails={gapDetails}
          />

          <AssignTasks
            visible={isModalVisible && activeModal === 'assign'}
            onCancel={() => setIsModalVisible(false)}
            //onSave={() => setIsModalVisible(false)}
            selectedIssue={selectedIssue}
            issueAreas={issueAreas}
            loading={modalLoading}
            api={api}
            onTaskAdded={handleTaskAdded}
          />

          <ViewTasks
            visible={isModalVisible && activeModal === 'view'}
            onCancel={() => setIsModalVisible(false)}
            selectedIssue={selectedIssue}
            loading={modalLoading}
            api={api}
            tasks={tasks}
            setTasks={setTasks}
            issueAreas={issueAreas}
            canGapsUpdate={canGapsUpdate}
          />
          
          <Row style={{ marginTop: 80 }}>
            <Assetia
              pageTitle="del detalle del GAP"
              pageDescription="ver el detalle de los GAPs generados"
              view="gaps"
            />
          </Row>
      </>
    </WithPermission>
  );
};

export default GapsDetail;